// Mutual exclusion between the channel groups of a time-unaligned CGR combo
// flight. When a 30P/30C pair merges without a trustworthy RPM-edge clock sync
// (comboAlignment === "unaligned"), the 30C-sourced channels sit on their own
// clock and may be shifted left/right relative to the 30P timeline. Showing the
// two groups together would invite false visual correlations, so the explorer
// enforces one-group-at-a-time: enabling a 30C channel turns off every active
// 30P channel and vice versa.

// Mutates `activeSeries` (a Set of channel keys) so it contains only one group.
// - `auxKeys`: the 30C-sourced channel keys for the loaded flight (Set or
//   array); empty means the flight is aligned and this is a no-op.
// - `lastChangedKeys`: the key(s) the user just enabled — the group they belong
//   to wins. Without them (e.g. restoring a saved route), the group with more
//   active channels wins, preferring the 30P primaries on a tie.
// Returns { removedKeys, keptGroup } where keptGroup is "aux" | "primary" when
// channels were removed and null when nothing needed to change.
export function enforceUnalignedSeriesExclusivity(activeSeries, auxKeys, lastChangedKeys = []) {
  const aux = auxKeys instanceof Set ? auxKeys : new Set(auxKeys ?? []);
  const removedKeys = [];
  if (!aux.size || !activeSeries?.size) return { removedKeys, keptGroup: null };
  const activeAux = [...activeSeries].filter((key) => aux.has(key));
  const activePrimary = [...activeSeries].filter((key) => !aux.has(key));
  if (!activeAux.length || !activePrimary.length) return { removedKeys, keptGroup: null };
  const changed = (Array.isArray(lastChangedKeys) ? lastChangedKeys : [lastChangedKeys])
    .filter((key) => key && activeSeries.has(key));
  const keepAux = changed.length
    ? changed.every((key) => aux.has(key))
    : activeAux.length > activePrimary.length;
  for (const key of keepAux ? activePrimary : activeAux) {
    activeSeries.delete(key);
    removedKeys.push(key);
  }
  return { removedKeys, keptGroup: keepAux ? "aux" : "primary" };
}
